import upperFirst from "lodash/upperFirst";
import React from "react";

import { Bolt } from "./Bolt";
import { Checkmark } from "./Checkmark";
import { Clock } from "./Clock";
import { Close } from "./Close";
import { Disconnected } from "./Disconnected";
import { Empty } from "./Empty";
import { Exit } from "./Exit";
import { Open } from "./Open";
import { Shimmer } from "./Shimmer";
import { Warning } from "./Warning";
import { Icon } from "./Icon";

export const lookupStatusConfig = (status?: string) => {
  switch (status) {
    case "admin":
    case "platform-admin":
    case "member":
    case "platform-member":
      return {
        icon: null,
        color: "bg-gray-200 text-gray-800 fill-gray-800",
      };
    case "pending":
      return {
        icon: Clock,
        color: "bg-gray-200 text-gray-800 fill-gray-800",
      };
    case "opened":
      return {
        icon: Open,
        color: "bg-blue-200 text-blue-800 fill-blue-800",
      };
    case "processing":
      return {
        icon: Clock,
        color: "bg-blue-200 text-blue-800 fill-blue-800",
      };
    case "active":
    case "success":
    case "completed":
    case "pass":
    case "active-basic":
      return {
        icon: Checkmark,
        color: "bg-green-200 text-green-800 fill-green-800",
      };
    case "inactive":
      return {
        icon: Close,
        color: "bg-red-200 text-red-800 fill-red-800",
        width: "w-4",
        height: "h-4",
      };
    case "unsupported":
      return {
        icon: Empty,
        color: "bg-gray-200 text-gray-800 fill-gray-800",
      };

    // Connection status of policy
    case "connection-inactive":
    case "credentials-expired":
      return {
        icon: Empty,
        color: "bg-gray-200 text-gray-800 fill-gray-800",
      };
    case "error":
    case "carrier-error":
    case "errored":
    case "manual":
    case "warning":
    case "caution":
    case "attempted":
      return {
        icon: Warning,
        color: "bg-amber-200 text-amber-800 fill-amber-800",
      };
    case "no-policies":
      return {
        icon: Empty,
        color: "bg-amber-200 text-amber-800 fill-amber-800",
      };
    case "exited":
      return {
        icon: Exit,
        color: "bg-amber-200 text-amber-800 fill-amber-800",
      };
    case "failed":
      return {
        icon: Close,
        color: "bg-amber-200 text-amber-800 fill-amber-800",
      };
    case "fail":
      return {
        icon: Close,
        color: "bg-red-200 text-red-800 fill-red-800",
      };
    case "disconnected":
      return {
        icon: Disconnected,
        color: "bg-amber-200 text-amber-800 fill-amber-800",
      };
    case "matched":
      return {
        icon: Bolt,
        width: "w-4",
        height: "h-4",
      };
    case "shimmer":
      return {
        icon: Shimmer,
        width: "w-4",
        height: "h-4",
      };
    case "unknown":
    default:
      return {
        icon: Clock,
        color: "bg-gray-200 text-gray-800 fill-gray-800",
      };
  }
};

type BadgeProps = {
  status?: string;
  withIcon?: boolean;
};

export const Badge = ({ status, withIcon = true }: BadgeProps) => {
  const statusConfig = lookupStatusConfig(status);
  let badgeText = status ? upperFirst(status.replace("-", " ")) : "Unknown";
  // Override badge text for connection-inactive status, temporary for NAIA
  if (status === "connection-inactive") {
    badgeText = "Inactive";
  }
  if (status === "active-basic") {
    badgeText = "Active - basic";
  }

  return (
    <div
      className={`flex gap-x-1.5 items-center rounded px-1.5 py-1 max-w-fit ${statusConfig.color}`}
    >
      {withIcon && statusConfig.icon && (
        <Icon
          icon={statusConfig.icon}
          height={statusConfig.height || "h-3"}
          width={statusConfig.width || "w-3"}
          className={
            statusConfig.color
              ? statusConfig.color
                  .split(" ")
                  .find((cls) => cls.includes("fill-"))
              : ""
          }
        />
      )}
      {status && <p className="font-medium text-xs">{badgeText}</p>}
    </div>
  );
};
